/**
 * @file src/_modulos/prenota/tabela/layouts/filters/TabItem.tsx
 * @description Componente simplificado de tab individual
 *
 * Princípios aplicados:
 * - KISS: Lógica simples e direta
 * - SRP: Apenas renderiza uma tab
 * - YAGNI: Remove funcionalidades não utilizadas
 */

"use client";

import { motion } from "motion/react";
import {
  TableStatusIndicator,
  TAB_CONFIGS,
  getTabColorByIndex,
} from "@PN/tabela";

/**
 * Props do componente TabItem
 */
interface TabItemProps {
  tabConfig: (typeof TAB_CONFIGS)[number];
  isActive: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  index: number;
  semBoletoCount?: number;
  isBoletoBadgeLoading?: boolean;
  // Props para dados reais
  isLoading?: boolean;
  data?: any[];
  lastUpdated?: Date;
  onRefresh?: () => void;
}

/**
 * Componente de tab individual
 */
export function TabItem({
  tabConfig,
  isActive,
  onClick,
  onMouseEnter,
  onMouseLeave,
  index,
  semBoletoCount = 0,
  isBoletoBadgeLoading = false,
  isLoading = false,
  data = [],
  lastUpdated = new Date(),
  onRefresh,
}: TabItemProps) {
  const currentColor = getTabColorByIndex(index);
  const showBadge = tabConfig.showBadge === true;
  const badgeCount = Math.max(0, Number(semBoletoCount) || 0);
  const badgeClass =
    badgeCount > 0
      ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800"
      : "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800";

  return (
    <div
      className={`px-4 py-3 cursor-pointer flex items-center gap-3 text-sm font-medium transition-all duration-200 min-w-[150px] rounded-lg justify-between relative overflow-hidden ${
        isActive
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {/* Gradiente animado de loading */}
      {isLoading && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ x: "-100%" }}
          animate={{ x: "100%" }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div className="w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        </motion.div>
      )}

      {/* Conteúdo centralizado (ícone + título) */}
      <div className="flex items-center gap-3 flex-1 relative z-10">
        <div
          className={`flex items-center justify-center w-5 h-5 ${
            isActive ? currentColor.icon : "text-muted-foreground"
          }`}
        >
          <tabConfig.icon className="h-4 w-4" />
        </div>

        <span
          className={`min-w-0 truncate ${isActive ? currentColor.text : ""}`}
        >
          {tabConfig.label}
        </span>
        {showBadge && (
          <span
            className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[11px] font-semibold border ${badgeClass}`}
            title={`Notas sem boleto: ${badgeCount}`}
          >
            {isBoletoBadgeLoading ? "..." : badgeCount}
          </span>
        )}
      </div>

      {/* Indicador de status - apenas para tab ativa */}
      {isActive && (
        <div className="flex-shrink-0 relative z-10">
          <TableStatusIndicator
            isLoading={isLoading}
            isStale={false}
            hasCachedData={data.length > 0}
            lastUpdated={lastUpdated}
            onRefresh={onRefresh}
            tabColor={currentColor.primary}
          />
        </div>
      )}
    </div>
  );
}
